import { SessionAlertsApi, SessionAlertStatus, SessionAlertManualState } from "@simbelapp/order-sdk";
import type { GlobalSessionAlertsCard, AlertsLifecycleSummary } from "@simbelapp/order-sdk";
import {
  AlertScope,
  AlertStatus,
  AlertTab,
  SessionAlertType,
  AlertTag,
  AlertTagSingular,
} from "~/utils/enums/alerts.enums";
import type {
  IAlert,
  IAlertCallbacks,
  IAlertDescriptionParams,
  IAlertTagMapping,
} from "~/utils/interfaces/alerts-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useFormats } from "~/composables/utils/useFormat";
import { useAuthStore } from "~/store/auth/auth.store";

const alertTagMap: IAlertTagMapping[] = [
  {
    name: SessionAlertType.Unavailability,
    value: AlertTag.UNAVAILABILITY,
    singularValue: AlertTagSingular.UNAVAILABILITY,
    type: "grey",
    logo: "warning",
  },
  {
    name: SessionAlertType.SessionNotSummoned,
    value: AlertTag.SESSION_NOT_SUMMONED,
    singularValue: AlertTagSingular.SESSION_NOT_SUMMONED,
    type: "grey",
    logo: "warning",
  },
  {
    name: SessionAlertType.AttendeeNotSummoned,
    value: AlertTag.ATTENDEE_NOT_SUMMONED,
    singularValue: AlertTagSingular.ATTENDEE_NOT_SUMMONED,
    type: "grey",
    logo: "warning",
  },
  {
    name: SessionAlertType.MinimumCapacityNotReached,
    value: AlertTag.MINIMUM_CAPACITY_NOT_REACHED,
    singularValue: AlertTagSingular.MINIMUM_CAPACITY_NOT_REACHED,
    type: "grey",
    logo: "warning",
  },
  {
    name: SessionAlertType.AttendanceNotDeclared,
    value: AlertTag.ATTENDANCE_NOT_DECLARED,
    singularValue: AlertTagSingular.ATTENDANCE_NOT_DECLARED,
    type: "grey",
    logo: "warning",
  },
];

// Global alert center — grouped by session, with date and count
const globalDescriptionMap: Record<SessionAlertType, (params: IAlertDescriptionParams) => string> = {
  [SessionAlertType.AttendanceNotDeclared]: ({ startDate }) =>
    `La session du ${startDate} a des présences non déclarées.`,

  [SessionAlertType.MinimumCapacityNotReached]: ({ startDate }) =>
    `La session du ${startDate} n'a pas atteint la capacité minimale.`,

  [SessionAlertType.Unavailability]: ({ startDate, nbAttendees }) =>
    `${nbAttendees} participant${(nbAttendees ?? 0) > 1 ? "s sont indisponibles" : " est indisponible"} pour la session du ${startDate}.`,

  [SessionAlertType.AttendeeNotSummoned]: ({ startDate, nbAttendees }) =>
    `${nbAttendees} participant${(nbAttendees ?? 0) > 1 ? "s n'ont pas été convoqués" : " n'a pas été convoqué"} pour la session du ${startDate}.`,

  [SessionAlertType.SessionNotSummoned]: ({ startDate }) => `La session du ${startDate} n'a pas été convoquée.`,
};

// Session alert center — ungrouped, per attendee with name
const sessionDescriptionMap: Record<SessionAlertType, (params: IAlertDescriptionParams) => string> = {
  [SessionAlertType.AttendanceNotDeclared]: () => `La session a des présences non déclarées.`,

  [SessionAlertType.MinimumCapacityNotReached]: () => `La session n'a pas atteint la capacité minimale.`,

  [SessionAlertType.Unavailability]: ({ attendeeName }) => `${attendeeName} a indiqué être indisponible`,

  [SessionAlertType.AttendeeNotSummoned]: ({ attendeeName }) => `${attendeeName} n'a pas été convoqué(e)`,

  [SessionAlertType.SessionNotSummoned]: () => `La session n'a pas été convoquée.`,
};

// Map SDK lifecycle to our AlertStatus
const mapLifecycleToStatus = (lifecycle: AlertsLifecycleSummary): AlertStatus => {
  if (lifecycle.status === SessionAlertStatus.Resolved) return AlertStatus.RESOLVED;
  if (lifecycle.manualState === SessionAlertManualState.Archived) return AlertStatus.ARCHIVED;
  return AlertStatus.ACTIVE;
};

export const useAlerts = () => {
  const feedback = useFeedback();
  const formats = useFormats();

  const getAlertTag = (type: SessionAlertType): IAlertTagMapping | undefined =>
    alertTagMap.find((x) => x.name === type);

  const getAlertDescription = (
    type: SessionAlertType,
    params: IAlertDescriptionParams,
    scope: AlertScope = AlertScope.GLOBAL,
  ): string => {
    const map = scope === AlertScope.SESSION ? sessionDescriptionMap : globalDescriptionMap;
    return map[type](params);
  };

  const formatDate = (isoDate: string): string => {
    return formats.toDateDisplay(isoDate) ?? isoDate;
  };

  // Map SDK GlobalSessionAlertsCard → IAlert
  const mapGlobalCard = (card: GlobalSessionAlertsCard): IAlert => ({
    id: card.groupKey,
    type: card.alertType as SessionAlertType,
    status: mapLifecycleToStatus(card.lifecycle),
    title: card.session.sessionTitle,
    metadata: {
      sessionId: card.session.sessionId,
      startDate: formatDate(card.session.startDate),
      endDate: formatDate(card.session.endDate),
      nbAttendees: card.affectedCount,
      archivedAt: card.lifecycle.archivedAt ? formatDate(card.lifecycle.archivedAt) : undefined,
      archivedBy:
        card.lifecycle.manualState === SessionAlertManualState.Archived
          ? (card.lifecycle.resolvedBy ?? undefined)
          : undefined,
      resolvedAt: card.lifecycle.resolvedAt ? formatDate(card.lifecycle.resolvedAt) : undefined,
      canArchive: card.canArchive,
      canReactivate: card.canReactivate,
    },
  });

  // getGlobalCallbacks() → callbacks for the global alert center (header bell)
  const getGlobalCallbacks = (): IAlertCallbacks => ({
    fetchAlerts: async (tab: AlertTab, offset: number, limit: number) => {
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, SessionAlertsApi);
        const response = await api.getSessionAlertsGlobalFeed({ tab, offset, limit });
        return {
          data: response.data.map(mapGlobalCard),
          total: (response.meta as any)?.count ?? response.data.length,
        };
      } catch {
        feedback.error("Impossible de charger les alertes", "small");
        return { data: [], total: 0 };
      }
    },
    archiveAlert: async (groupKey: string) => {
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, SessionAlertsApi);
        await api.archiveSessionAlerts({ sessionAlertsActionDto: { groupKey } });
      } catch (error) {
        feedback.error("Impossible d'archiver l'alerte", "small");
        throw error;
      }
    },
    archiveAll: async () => {
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, SessionAlertsApi);
        await api.archiveAllSessionAlerts();
      } catch (error) {
        feedback.error("Impossible d'archiver toutes les alertes", "small");
        throw error;
      }
    },
    reactivateAlert: async (groupKey: string) => {
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, SessionAlertsApi);
        await api.reactivateSessionAlerts({ sessionAlertsActionDto: { groupKey } });
      } catch (error) {
        feedback.error("Impossible de réactiver l'alerte", "small");
        throw error;
      }
    },
    getAlertLink: (alert: IAlert) => {
      const authStore = useAuthStore();
      if (!authStore.isCompanyAdmin && (authStore.isTrainingPlanAdmin || authStore.isTrainingAdmin)) {
        return `/my-sessions?id=${alert.metadata.sessionId}&type=session`;
      }

      return `/sessions?id=${alert.metadata.sessionId}&type=session`;
    },
  });

  // getAlertSettingsConfig() → static config for each alert type used in the settings page
  const getAlertSettingsConfig = () => [
    {
      type: SessionAlertType.Unavailability,
      title: "Participants indisponibles",
      description:
        "Alerte automatique si un participant est indisponible sur la session. Ne s'applique pas aux sessions eLearning.",
      resolution:
        "Archivée automatiquement si le conflit calendrier disparaît, si le participant répond présent, ou si le participant est retiré",
      hasDelay: false,
      delayLabel: null,
    },
    {
      type: SessionAlertType.SessionNotSummoned,
      title: "Session non convoquée",
      description:
        "Alerte automatique si la session est encore en « Planification en cours » et aucune convocation n'a été envoyée au délai configuré.",
      resolution: "Archivée automatiquement au passage en « Convoquée » ou si la formation a démarré",
      hasDelay: true,
      delayLabel: "jours avant la date début",
    },
    {
      type: SessionAlertType.AttendeeNotSummoned,
      title: "Participant(s) non convoqué(s)",
      description:
        "Alerte automatique si un participant inscrit à une session déjà convoquée n'a pas reçu de convocation.",
      resolution: "Archivée automatiquement quand une convocation est envoyée au participant",
      hasDelay: false,
      delayLabel: null,
    },
    {
      type: SessionAlertType.MinimumCapacityNotReached,
      title: "Capacité minimale non atteinte",
      description:
        "Alerte automatique si le nombre de participants confirmés est inférieur à la capacité minimale définie sur la session.",
      resolution: "Archivée automatiquement quand nb participants ≥ capacité minimale",
      hasDelay: true,
      delayLabel: "jours avant la date début",
    },
    {
      type: SessionAlertType.AttendanceNotDeclared,
      title: "Présences non déclarées",
      description:
        "Alerte automatique si la session est au statut « Non-déclarée terminée », les présences n'ont pas été saisies sur toutes les étapes",
      resolution: "Archivée automatiquement au passage en « Déclarée terminée »",
      hasDelay: true,
      delayLabel: "jours après la date de fin",
    },
  ];

  return {
    getAlertTag,
    getAlertDescription,
    getGlobalCallbacks,
    getAlertSettingsConfig,
  };
};
